<template>
  <div class="min-h-screen bg-slate-50 text-slate-900 pb-32 animate-fade-in-up">
    <!-- Back Button -->
    <button
      @click="onBack"
      class="fixed top-8 left-8 z-50 p-2 text-slate-400 hover:text-slate-600 transition-colors"
    >
      <IconChevronLeft class-name="w-6 h-6" />
    </button>

    <!-- 主体内容区 -->
    <div class="max-w-3xl mx-auto px-4 py-8">

      <!-- 卡片1: 顶部信息区 -->
      <section class="bg-white rounded-lg shadow-sm p-6 mb-3">
        <div class="flex items-start gap-6">
          <!-- 头像 -->
          <div
            class="relative cursor-pointer group flex-shrink-0"
            @click="isAvatarExpanded = true"
          >
            <img
              :src="expert.avatar"
              :alt="expert.name"
              class="w-20 h-20 rounded-full object-cover border-2 border-slate-200 group-hover:border-[#F2C94C] transition-colors"
              referrerpolicy="no-referrer"
            />
            <div class="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <span class="opacity-0 group-hover:opacity-100 text-white text-xs font-medium">查看</span>
            </div>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-2xl font-bold text-slate-900 mb-1">{{ expert.name }}</h1>
                <p class="text-sm text-slate-500 mb-1">
                  {{ expert.department ? `${expert.department} · ${expert.position || expert.title}` : expert.title }}
                </p>
                <div class="flex items-center gap-1 text-sm text-slate-400">
                  <IconMapPin class-name="w-4 h-4" />
                  <span>北京</span>
                </div>
              </div>

              <!-- 数据展示 -->
              <div class="flex items-center gap-6 text-right">
                <div>
                  <div class="text-xl font-bold text-slate-900">{{ expert.stats.prescriptions }}</div>
                  <div class="text-xs text-slate-400">次贡献</div>
                </div>
                <div>
                  <div class="text-xl font-bold text-slate-900">{{ totalLikesAndBookmarks }}</div>
                  <div class="text-xs text-slate-400">获赞</div>
                </div>
                <div>
                  <div class="text-xl font-bold text-[#F2C94C]">{{ expert.points }}</div>
                  <div class="text-xs text-slate-400">专家积分</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分割线 -->
        <div class="border-t border-slate-100 my-4"></div>

        <!-- 擅长领域 -->
        <div>
          <h3 class="text-sm font-semibold text-slate-900 mb-3">擅长领域</h3>
          <div class="flex flex-wrap gap-4">
            <span v-for="(area, index) in expertiseAreas" :key="index" class="text-sm text-slate-600">{{ area }}</span>
          </div>
        </div>
      </section>

      <!-- 卡片2: 职业履历 -->
      <section class="bg-white rounded-lg shadow-sm p-6 mb-3">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-slate-900">职业履历</h2>
          <button
            @click="showAllResume = !showAllResume"
            class="text-sm text-slate-400 hover:text-slate-600 transition-colors"
          >
            {{ showAllResume ? '[ 收起 ]' : '[ 展开 ]' }}
          </button>
        </div>

        <!-- 金色分隔线 -->
        <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

        <!-- 简介 -->
        <div class="mb-6">
          <p v-for="(p, i) in bioParagraphs" :key="i" class="text-slate-600 text-sm leading-relaxed">{{ p }}</p>
        </div>

        <!-- 详细履历 -->
        <div class="space-y-5">
          <div class="pl-4 border-l-2 border-slate-200">
            <div class="flex items-baseline gap-2 mb-1">
              <span class="font-medium text-slate-900">华为技术有限公司</span>
              <span class="text-slate-400">|</span>
              <span class="text-sm text-slate-600">组织发展总监（15年）</span>
            </div>
            <p class="text-slate-500 text-sm leading-relaxed">
              负责BG级战略解码与绩效体系重构，主导过3次千人级组织变革，沉淀出一套完整的「目标穿透方法论」。
            </p>
          </div>

          <div v-if="showAllResume" class="pl-4 border-l-2 border-slate-200">
            <div class="flex items-baseline gap-2 mb-1">
              <span class="font-medium text-slate-900">前阿里巴巴</span>
              <span class="text-slate-400">|</span>
              <span class="text-sm text-slate-600">HRBP总监（8年）</span>
            </div>
            <p class="text-slate-500 text-sm leading-relaxed">
              负责集团人才发展体系建设，主导设计了管理干部培养体系，支撑业务从500人扩张到5000人。
            </p>
          </div>
        </div>
      </section>

      <!-- 卡片3: 过往作品集 -->
      <section class="bg-white rounded-lg shadow-sm p-6 mb-3">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-slate-900">过往作品集（{{ expertTopics.length }}）</h2>
          <button class="text-sm text-slate-400 hover:text-slate-600 transition-colors">
            [ 查看全部 ]
          </button>
        </div>

        <!-- 金色分隔线 -->
        <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

        <div class="space-y-0">
          <div
            v-for="(topic, index) in expertTopics"
            :key="topic.id"
            class="py-3 border-b border-slate-50 last:border-b-0 group cursor-pointer"
            @click="handleTopicClick(topic.id)"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span class="text-slate-300 text-xs w-5">{{ String(index + 1).padStart(2, '0') }}</span>
                <h3 class="text-sm text-slate-700 group-hover:text-slate-900 transition-colors">
                  {{ topic.title }}
                </h3>
              </div>
              <div class="flex items-center gap-2">
                <button
                  v-if="topic.hasVideo"
                  @click.stop="navigateToCase('video')"
                  class="text-slate-300 hover:text-[#F2C94C] transition-colors"
                  title="视频"
                >
                  <IconVideo class-name="w-4 h-4" />
                </button>
                <button
                  v-if="topic.hasAudio"
                  @click.stop="navigateToCase('audio')"
                  class="text-slate-300 hover:text-[#F2C94C] transition-colors"
                  title="音频"
                >
                  <IconHeadphones class-name="w-4 h-4" />
                </button>
                <button
                  v-if="topic.hasDoc"
                  @click.stop="navigateToCase('text')"
                  class="text-slate-300 hover:text-[#F2C94C] transition-colors"
                  title="图文"
                >
                  <IconFileText class-name="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 卡片4: 用户评价 -->
      <section class="bg-white rounded-lg shadow-sm p-6 mb-3">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-slate-900">用户评价（128）</h2>
          <button class="text-sm text-slate-400 hover:text-slate-600 transition-colors">
            [ 写评价 ]
          </button>
        </div>

        <!-- 金色分隔线 -->
        <div class="w-12 h-0.5 bg-[#F2C94C] mb-4"></div>

        <div class="space-y-4">
          <div v-for="review in expertReviews" :key="review.id" class="pb-4 border-b border-slate-50 last:border-b-0 last:pb-0">
            <div class="flex items-center gap-2 mb-2">
              <div class="flex">
                <IconStar
                  v-for="i in review.rating"
                  :key="i"
                  class-name="w-3.5 h-3.5 fill-[#F2C94C] text-[#F2C94C]"
                />
              </div>
              <span class="text-slate-900 font-medium text-sm">{{ review.name }}</span>
              <span class="text-slate-400">·</span>
              <span class="text-slate-400 text-xs">{{ review.title }}</span>
            </div>
            <p class="text-slate-600 text-sm leading-relaxed">"{{ review.content }}"</p>
          </div>
        </div>
        <button class="mt-4 text-sm text-slate-400 hover:text-slate-600 transition-colors">
          [ 查看全部 128 条评价 ]
        </button>
      </section>
    </div>

    <!-- 右下角悬浮预约按钮 -->
    <button
      @click="isModalOpen = true"
      class="fixed bottom-6 right-6 z-50 px-5 py-3 bg-[#F2C94C] text-white rounded-full font-medium shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center gap-2"
    >
      <IconMessageSquare class-name="w-4 h-4" />
      <span>预约</span>
      <span class="text-white/80 text-sm">300积分</span>
    </button>

    <!-- 头像放大 Modal -->
    <transition name="fade">
      <div
        v-if="isAvatarExpanded"
        class="fixed inset-0 z-[300] bg-black/80 flex items-center justify-center p-8"
        @click="isAvatarExpanded = false"
      >
        <transition name="scale-in" appear>
          <div class="relative" @click.stop>
            <button
              @click="isAvatarExpanded = false"
              class="absolute -top-12 right-0 text-white/80 hover:text-white"
            >
              <IconX class-name="w-8 h-8" />
            </button>
            <img
              :src="expert.avatar"
              :alt="expert.name"
              class="w-80 h-80 rounded-full object-cover border-4 border-white"
              referrerpolicy="no-referrer"
            />
          </div>
        </transition>
      </div>
    </transition>

    <!-- 预约 Modal -->
    <transition name="fade">
      <div
        v-if="isModalOpen"
        class="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
        @click="isModalOpen = false"
      >
        <transition name="scale-in" appear>
          <div class="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl" @click.stop>
            <div v-if="!showSuccess">
              <!-- Header -->
              <div class="flex items-center gap-4 mb-8">
                <img
                  :src="expert.avatar"
                  :alt="expert.name"
                  class="w-14 h-14 rounded-full object-cover border-2 border-[#F2C94C]"
                  referrerpolicy="no-referrer"
                />
                <div>
                  <div class="text-lg font-bold text-slate-900">预约 {{ expert.name }}</div>
                  <div class="text-sm text-slate-400">300 积分/次</div>
                </div>
              </div>

              <!-- 预约主题 -->
              <div class="space-y-3 mb-6">
                <div class="text-sm font-bold text-slate-900">预约主题</div>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="theme in themeOptions"
                    :key="theme"
                    @click="selectedTheme = theme"
                    :class="[
                      'px-4 py-2 rounded-xl text-sm transition-colors',
                      selectedTheme === theme
                        ? 'bg-[#F2C94C] text-white'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                    ]"
                  >
                    {{ theme }}
                  </button>
                </div>
              </div>

              <!-- 连线方式 -->
              <div class="space-y-3 mb-8">
                <div class="text-sm font-bold text-slate-900">连线方式</div>
                <div class="flex gap-2">
                  <button
                    v-for="method in methodOptions"
                    :key="method.id"
                    @click="selectedMethod = method.id"
                    :class="[
                      'flex-1 px-4 py-3 rounded-xl text-sm transition-colors flex items-center justify-center gap-2',
                      selectedMethod === method.id
                        ? 'bg-[#F2C94C] text-white'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                    ]"
                  >
                    <component :is="method.iconComponent" class-name="w-4 h-4" />
                    {{ method.label }}
                  </button>
                </div>
              </div>

              <!-- 扣费提示 -->
              <div class="p-4 bg-slate-50 rounded-xl mb-6">
                <p class="text-sm text-slate-600">
                  本次预约将消耗您的 <span class="font-bold text-[#F2C94C]">300 战术积分</span>
                </p>
                <p class="text-xs text-slate-400 mt-1">
                  当前余额：{{ userPoints }} 积分
                </p>
              </div>

              <!-- 确认按钮 -->
              <button
                @click="handleConfirmBooking"
                class="w-full py-4 bg-[#F2C94C] text-white rounded-2xl font-bold hover:bg-[#e5b73c] transition-colors"
              >
                确认预约
              </button>
            </div>

            <!-- 成功状态 -->
            <div v-else class="py-12 flex flex-col items-center text-center">
              <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <IconCheck class-name="w-10 h-10 text-green-500" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-2">指令已下达</h3>
              <p class="text-sm text-slate-400">预约成功，专家将尽快与您联系</p>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  IconChevronLeft,
  IconMessageSquare,
  IconVideo,
  IconHeadphones,
  IconFileText,
  IconCheck,
  IconPhone,
  IconMapPin,
  IconStar,
  IconX
} from '../icons'

// 模拟话题数据
const EXPERT_TOPICS = [
  { id: '1', title: '核心员工离职预警与挽留', hasVideo: true, hasAudio: true, hasDoc: true },
  { id: '2', title: '如何拆解快速扩张期的团队目标', hasVideo: true, hasAudio: false, hasDoc: true },
  { id: '3', title: '执行力破局：从推一下动一下到自驱动', hasVideo: false, hasAudio: true, hasDoc: true },
  { id: '4', title: '战略解码：如何把公司目标穿透到个人', hasVideo: true, hasAudio: true, hasDoc: false }
]

// 模拟评价数据
const EXPERT_REVIEWS = [
  {
    id: '1',
    name: '张经理',
    title: '产品总监',
    rating: 5,
    content: '李老师的目标穿透方法论帮我们在3个月内将团队执行力提升了40%，非常实战的指导！'
  },
  {
    id: '2',
    name: '王总监',
    title: '技术VP',
    rating: 5,
    content: '不玩虚的，直接给可落地的方案。对于快速扩张期的团队管理很有启发。'
  }
]

export default {
  name: 'ExpertProfileView',
  components: {
    IconChevronLeft,
    IconMessageSquare,
    IconVideo,
    IconHeadphones,
    IconFileText,
    IconCheck,
    IconPhone,
    IconMapPin,
    IconStar,
    IconX
  },
  props: {
    expert: {
      type: Object,
      required: true
    },
    onBack: {
      type: Function,
      required: true
    },
    onBook: {
      type: Function,
      required: true
    },
    onViewCase: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      isModalOpen: false,
      selectedTheme: '人才留存',
      selectedMethod: '语音电话',
      showSuccess: false,
      userPoints: 1200,
      isAvatarExpanded: false,
      showAllResume: false,
      expertTopics: EXPERT_TOPICS,
      expertReviews: EXPERT_REVIEWS,
      themeOptions: ['人才留存', '执行力破局', '战略解码'],
      methodOptions: [
        { id: '语音电话', iconComponent: 'IconPhone', label: '语音电话 (1对1通话)' },
        { id: '线下约见', iconComponent: 'IconMapPin', label: '线下约见' }
      ]
    }
  },
  computed: {
    totalLikesAndBookmarks() {
      return (this.expert.stats.likes + this.expert.stats.bookmarks).toLocaleString()
    },
    expertiseAreas() {
      return this.expert.topics?.slice(0, 4) || ['组织变革', '绩效管理', '团队搭建', '战略解码']
    },
    bioParagraphs() {
      return this.expert.bio ? this.expert.bio.split('\n') : []
    }
  },
  methods: {
    handleConfirmBooking() {
      this.showSuccess = true
      setTimeout(() => {
        this.userPoints -= 300
        this.isModalOpen = false
        this.showSuccess = false
        this.onBook()
      }, 2000)
    },
    handleTopicClick(topicId) {
      this.$router.push(`/topic/${topicId}`)
    },
    navigateToCase(type) {
      this.$router.push(`/expert/${this.expert.id}/case/c1?type=${type}&from=expert`)
    }
  }
}
</script>

<style scoped>
.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.scale-in-enter-active,
.scale-in-leave-active {
  transition: all 0.2s ease;
}

.scale-in-enter,
.scale-in-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
